/* eslint-disable react/require-default-props */
import React from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import { useWindowResize } from '../../../hooks/useWindowSize'
import breakpoints from '../../../theme/breakpoints'
import { CircleIcon, Circle, CircleImage } from '../Circle/Circle'
import { Typography } from '../Typography/Typography'

type RuleNames = 'column' | 'titleContainer' | 'rowContainer' | 'hr'

interface StyleProps {
    gridTemplateColumns?: string
    iconWidth?: string
}

export type Row = {
    label: string
    value: string | number
    profilePicture?: {
        content: string
        mimeType: string
    }
    useContent?: boolean
    content?: JSX.Element
    mailToLinkIcon?: JSX.Element
}

type Props = {
    gridTemplateColumns?: string
    titleLabel?: string
    titleIcon?: JSX.Element
    rows?: Row[]
    iconWidth?: string
    hideTitle?: boolean
}

export const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>(
    () => ({
        column: ({ gridTemplateColumns }) => {
            return {
                backgroundColor: colors.white,
                width: '100%',
                gridTemplateColumns,
                borderRadius: '.5rem',
                height: 'fit-content',
                marginBottom: '20px',
                minWidth: 0,
            }
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '1rem',
        },
        rowContainer: {
            padding: '.75rem 1rem .75rem 1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            wordBreak: 'break-word',
            width: '100%',

            '& a': {
                display: 'inline-flex',
            },
        },
        hr: {
            opacity: '.4',
            margin: '0',
        },
    })
)

// TODO add possibility to have a tooltip - ? icon
const CardColumn: React.FC<Props> = ({
    gridTemplateColumns,
    titleIcon,
    titleLabel,
    iconWidth,
    hideTitle,
    rows = [
        {
            label: '',
            value: '',
            profilePicture: {
                content: null,
                mimeType: null,
            },
            mailToLinkIcon: null,
        },
    ],
}) => {
    const theme = useTheme<Jss.Theme>()
    const classes = useStyles({ ...{ gridTemplateColumns }, theme })
    return (
        <div className={classes.column}>
            {!hideTitle ? (
                <div className={classes.titleContainer}>
                    <Circle>
                        <CircleIcon iconWidth={iconWidth}>
                            {titleIcon}
                        </CircleIcon>
                    </Circle>

                    <Typography tag="h3" size="M" spacing=".2rem" weight="bold">
                        {titleLabel}
                    </Typography>
                </div>
            ) : null}
            {rows.map((row, index) => {
                if (row?.label?.length === 0 && !row?.useContent) {
                    return <></>
                }

                if (row.useContent && row.content) {
                    return (
                        <>
                            {!hideTitle || (hideTitle && index !== 0) ? (
                                <hr className={classes.hr} />
                            ) : null}
                            <div className={classes.rowContainer}>
                                {row.content}
                            </div>
                        </>
                    )
                }

                return (
                    <>
                        {!hideTitle || (hideTitle && index !== 0) ? (
                            <hr className={classes.hr} />
                        ) : null}
                        <div className={classes.rowContainer}>
                            <>
                                {row?.profilePicture?.content &&
                                    row?.profilePicture?.mimeType && (
                                        <CircleImage
                                            // eslint-disable-next-line max-len
                                            url={`${row?.profilePicture?.mimeType}${row?.profilePicture?.content}`}
                                        />
                                    )}
                            </>
                            <div>
                                <Typography
                                    tag="h4"
                                    size="XXS"
                                    color=" rgba(0, 0, 0, 0.4)"
                                >
                                    {row.label}
                                </Typography>
                                <Typography tag="p" size="XS">
                                    {row.value}
                                </Typography>
                            </div>
                            {row?.mailToLinkIcon && row.mailToLinkIcon}
                        </div>
                    </>
                )
            })}
        </div>
    )
}

export { CardColumn }
